<template>
    <div class="dashboard">
        <!-- Header Section -->
        <div class="greeting-section">
            <h2>{{ greeting }}, {{ userDisplayName }}!</h2>
        </div>

        <div class="dashboard-content">
            <!-- Left Column -->
            <div class="events-section">
                <div class="regular-meetings">
                    <h3>Upcoming Regular Meetings</h3>
                    <div class="meetings-list">
                        <div v-for="meeting in upcomingRegularMeetings" 
                             :key="meeting.id" 
                             class="meeting-item"
                             :class="{
                                 'not-checked-in': !getMeetingCheckInStatus(meeting.id),
                                 'checked-in-attending': getMeetingCheckInStatus(meeting.id) && getMeetingAttendanceStatus(meeting.id)?.attending,
                                 'checked-in-substitute': getMeetingCheckInStatus(meeting.id) && !getMeetingAttendanceStatus(meeting.id)?.attending && getMeetingAttendanceStatus(meeting.id)?.hasSubstitute,
                                 'checked-in-not-attending': getMeetingCheckInStatus(meeting.id) && !getMeetingAttendanceStatus(meeting.id)?.attending && !getMeetingAttendanceStatus(meeting.id)?.hasSubstitute
                             }"
                        >
                            <div class="meeting-header">
                                <button 
                                    class="notes-btn" 
                                    :class="{ 'has-notes': hasNotes(meeting.id) }"
                                    @click.stop="handleNotes(meeting)"
                                    :title="hasNotes(meeting.id) ? 'View Notes' : 'Add Notes'"
                                >
                                    <span v-if="hasNotes(meeting.id)">📝</span>
                                    <span v-else>✎</span>
                                </button>
                                <div class="meeting-info">
                                    <div class="meeting-date">{{ formatDate(meeting.event_date) }}</div>
                                    <div class="meeting-venue">{{ meeting.venue_name }}</div>
                                </div>
                                <button class="heat-map-btn" @click.stop="showHeatMap(meeting)">
                                    📊
                                </button>
                            </div>
                            <div class="meeting-footer">
                                <button 
                                    class="view-details-btn"
                                    @click="viewEventDetails(meeting)"
                                >
                                    View Details
                                </button>
                                <div class="check-in-status">
                                    <span v-if="getMeetingCheckInStatus(meeting.id)">
                                        ✓ {{ 
                                            getMeetingAttendanceStatus(meeting.id)?.attending ? 'Checked In & Attending' :
                                            getMeetingAttendanceStatus(meeting.id)?.hasSubstitute ? 'Checked In & Substitute Attending' :
                                            'Checked In & Not Attending'
                                        }}
                                    </span>
                                </div>
                                <div class="check-in-wrapper">
                                    <button 
                                        @click.stop="openCheckIn(meeting)"
                                        :class="[
                                            'check-in-btn',
                                            getMeetingCheckInStatus(meeting.id) ? 'edit-mode' : '',
                                            meeting !== upcomingRegularMeetings[0] ? 'disabled' : ''
                                        ]"
                                        :disabled="meeting !== upcomingRegularMeetings[0]"
                                    >
                                        {{ getMeetingCheckInStatus(meeting.id) ? 'Edit Check-in' : 'Check In' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="other-events">
                    <h3>Upcoming Events</h3>
                    <div class="events-list">
                        <div v-for="event in upcomingOtherEvents" 
                             :key="event.id" 
                             class="event-item"
                             @click="viewEventDetails(event)">
                            <div class="event-type" :class="getEventTypeClass(event.event_type)">
                                {{ event.event_type }}
                            </div>
                            <div class="event-date">{{ formatDate(event.event_date) }}</div>
                            <div class="event-venue">{{ event.venue_name }}</div>
                            <div class="event-address" v-if="event.venue_address">{{ event.venue_address }}</div>
                        </div>
                    </div>
                </div>

                <div class="card">
                    <UpcomingBirthdays />
                </div>
            </div>

            <!-- Right Column -->
            <div class="speaking-info">
                <TasksSummary :tasks="tasks" class="tasks-summary-widget" />
                <div class="next-speaking" :class="{ 'soon': isDueSoonToSpeak }">
                    <h3>You are next speaking</h3>
                    <div v-if="nextSpeaking">
                        <div class="date">{{ formatDate(nextSpeaking.event_date) }}</div>
                        <div class="role">{{ nextSpeaking.session_type === 'education' ? 'Education Speaker' : 'Member Speaker' }}</div>
                        <div class="days-until" v-if="daysUntilNextSpeaking < 7">
                            In {{ daysUntilNextSpeaking }} days
                        </div>
                    </div>
                    <div v-else>No upcoming speaking assignments</div>
                </div>

                <div class="check-in-history">
                    <h3>Check-in History</h3>
                    <CheckInScoreGraph v-if="user?.uid" :userId="user.uid" />
                </div>
                <GoalsSummary 
                    :goals="memberGoals"
                    @goalClick="navigateToGoal"
                />
            </div>
        </div>

        <!-- Event Details Modal -->
        <EventDetailsModal
            v-if="showDetailsModal"
            :event="selectedEvent"
            :current-user="userProfile"
            :formats="formats"
            @close="closeDetailsModal"
        />

        <!-- Check-in Modal -->
        <CheckInModal
            v-if="showCheckInModal"
            :event="selectedMeeting"
            :existingCheckIn="selectedMeeting?.existingCheckIn"
            @close="closeCheckInModal"
            @check-in="handleCheckInComplete"
        />

        <HeatMapModal
            v-if="showHeatMapModal"
            :meeting="selectedMeeting"
            @close="closeHeatMapModal"
        />

        <!-- Notes Modal -->
        <NotesModal
            v-if="showNotesModal"
            :event="selectedEventForNotes"
            @close="showNotesModal = false"
            @tasks-created="handleNewTasks"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { eventService } from '../services/eventService';
import { userService } from '../services/api';
import { checkinService } from '../services/checkinService';
import { noteService } from '../services/noteService';
import CheckInModal from './CheckInModal.vue';
import EventDetailsModal from './EventDetailsModal.vue';
import CheckInScoreGraph from './CheckInScoreGraph.vue';
import HeatMapModal from './HeatMapModal.vue';
import GoalsSummary from './GoalsSummary.vue';
import UpcomingBirthdays from './UpcomingBirthdays.vue';
import NotesModal from './notes/NotesModal.vue';
import TasksSummary from './TasksSummary.vue';
import { useRouter } from 'vue-router';
import { goalService } from '../services/goalService';
import { taskService } from '../services/taskService';

export default {
    name: 'UserDashboard',
    components: {
        CheckInModal,
        EventDetailsModal,
        CheckInScoreGraph,
        HeatMapModal,
        GoalsSummary,
        UpcomingBirthdays,
        NotesModal,
        TasksSummary
    },
    setup() {
        const user = ref(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null);
        const userProfile = ref(null);
        const events = ref([]);
        const error = ref(null);
        const showDetailsModal = ref(false);
        const selectedEvent = ref(null);
        const showCheckInModal = ref(false);
        const selectedMeeting = ref(null);
        const meetingCheckIns = ref(new Map()); // Store check-in data, not just status
        const showHeatMapModal = ref(false);
        const memberGoals = ref([]);
        const tasks = ref([]);
        const router = useRouter();

        // Notes state
        const eventNotes = ref({});  // Map of event ID to notes
        const showNotesModal = ref(false);
        const selectedEventForNotes = ref(null);
        const formats = ref([]);  // Add formats ref

        const userDisplayName = computed(() => userProfile.value?.display_name || user.value?.displayName || '');

        const loadUserProfile = async () => {
            try {
                const profile = await userService.getProfile();
                userProfile.value = profile;
            } catch (err) {
                // Error loading user profile
            }
        };

        const loadGoals = async () => {
            try {
                const profile = await userService.getProfile();
                const goals = await goalService.getMemberGoals(profile.id, new Date().getFullYear());
                
                const updatedGoals = await Promise.all(goals.map(async (goal) => {
                    const latestProgress = await goalService.getLatestProgress(goal.id);
                    if (latestProgress && latestProgress.current_value) {
                        goal.progress = Math.min((latestProgress.current_value / goal.target_value) * 100, 100);
                    } else {
                        goal.progress = 0;
                    }
                    return goal;
                }));
                
                memberGoals.value = updatedGoals;
            } catch (error) {
                // Error loading goals
            }
        };

        const loadTasks = async () => {
            try {
                const response = await taskService.getTasksForUser();
                tasks.value = response.tasks || [];
            } catch (error) {
                // Failed to load tasks
                tasks.value = [];
            }
        };

        const loadFormats = async () => {
            try {
                const response = await eventService.getFormats();
                formats.value = response;
            } catch (error) {
                // Error loading formats
            }
        };

        onMounted(async () => {
            await Promise.all([
                loadEvents(),
                loadUserProfile(),
                eventService.getAllMembers().then(response => {
                    members.value = response;
                }),
                loadGoals(),
                loadTasks(),
                loadFormats()  // Load formats on mount
            ]);
            await loadMeetingCheckIns();
        });

        // Time-based greeting
        const greeting = computed(() => {
            const hour = new Date().getHours();
            if (hour < 12) return 'Good Morning';
            if (hour < 17) return 'Good Afternoon';
            return 'Good Evening';
        });

        // Filter events by type
        const upcomingRegularMeetings = computed(() => {
            const now = new Date();
            return events.value
                .filter(event => {
                    if (event.event_type !== 'Regular Meeting') return false;
                    
                    const eventEnd = new Date(event.event_date);
                    // Add duration (default 2 hours if not specified)
                    eventEnd.setHours(eventEnd.getHours() + (event.duration || 2));
                    
                    return eventEnd > now;
                })
                .slice(0, 3); // Show next 3 meetings
        });

        const upcomingOtherEvents = computed(() => {
            return events.value
                .filter(event => 
                    event.event_type !== 'Regular Meeting' && 
                    new Date(event.event_date) > new Date()
                )
                .slice(0, 5); // Show next 5 events
        });

        // Speaking assignments
        const nextSpeaking = computed(() => {
            return events.value
                .find(event => 
                    event.event_type === 'Regular Meeting' &&
                    new Date(event.event_date) > new Date() &&
                    (event.education_session?.speaker_id === userProfile.value?.id ||
                     event.main_session?.speaker_id === userProfile.value?.id)
                );
        });

        const daysUntilNextSpeaking = computed(() => {
            if (!nextSpeaking.value) return null;
            const diff = new Date(nextSpeaking.value.event_date) - new Date();
            return Math.ceil(diff / (1000 * 60 * 60 * 24));
        });

        const isDueSoonToSpeak = computed(() => {
            return daysUntilNextSpeaking.value !== null && daysUntilNextSpeaking.value <= 7;
        });

        const lastSpoke = computed(() => {
            return [...events.value]
                .filter(event => 
                    event.event_type === 'Regular Meeting' &&
                    new Date(event.event_date) < new Date() &&
                    (event.education_session?.speaker_id === userProfile.value?.id ||
                     event.main_session?.speaker_id === userProfile.value?.id)
                )
                .sort((a, b) => new Date(b.event_date) - new Date(a.event_date))[0];
        });

        const getTimeAgo = (date) => {
            const diff = new Date() - new Date(date);
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            if (days === 0) return 'Today';
            if (days === 1) return 'Yesterday';
            if (days < 30) return `${days} days ago`;
            const months = Math.floor(days / 30);
            return `${months} ${months === 1 ? 'month' : 'months'} ago`;
        };

        const loadEvents = async () => {
            try {
                const response = await eventService.getAllEvents();
                events.value = response;
            } catch (err) {
                // Error loading events
                error.value = 'Failed to load events';
            }
        };

        const members = ref([]);

        const viewEventDetails = async (event) => {
            try {
                // Load formats and event details together
                const [, eventDetails] = await Promise.all([
                    loadFormats(),
                    eventService.getEventDetails(event.id)
                ]);
                selectedEvent.value = eventDetails;
                showDetailsModal.value = true;
            } catch (error) {
                // Error fetching event details
            }
        };

        const closeDetailsModal = () => {
            showDetailsModal.value = false;
            selectedEvent.value = null;
        };

        const getEventSpeakerName = (speakerId) => {
            const member = members.value.find(m => m.id === speakerId);
            return member ? `${member.first_name} ${member.last_name}` : 'Not assigned';
        };

        const closeCheckInModal = () => {
            showCheckInModal.value = false;
            nextTick(() => {
                selectedMeeting.value = null;
            });
        };

        const openCheckIn = (meeting) => {
            if (!meeting) {
                // No meeting provided to openCheckIn
                return;
            }
            selectedMeeting.value = {
                id: meeting.id,
                event_date: meeting.event_date,
                event_type: meeting.event_type,
                venue_name: meeting.venue_name,
                existingCheckIn: meetingCheckIns.value.get(meeting.id) || null
            };
            
            nextTick(() => {
                showCheckInModal.value = true;
            });
        };

        const handleCheckInComplete = async () => {
            await loadMeetingCheckIns();
            closeCheckInModal();
        };

        const loadMeetingCheckIns = async () => {
            for (const meeting of upcomingRegularMeetings.value) {
                try {
                    const checkIn = await checkinService.getMemberCheckin(meeting.id);
                    meetingCheckIns.value.set(meeting.id, checkIn);
                } catch (error) {
                    // Error loading check-in status
                }
            }
        };

        const getMeetingCheckInStatus = (meetingId) => {
            return !!meetingCheckIns.value.get(meetingId);
        };

        const getMeetingAttendanceStatus = (meetingId) => {
            const checkIn = meetingCheckIns.value.get(meetingId);
            if (!checkIn) return null;
            return {
                isCheckedIn: true,
                attending: checkIn.attending,
                hasSubstitute: !checkIn.attending && checkIn.has_substitute
            };
        };

        const showHeatMap = (meeting) => {
            selectedMeeting.value = meeting;
            showHeatMapModal.value = true;
        };

        const closeHeatMapModal = () => {
            showHeatMapModal.value = false;
            selectedMeeting.value = null;
        };

        // Load notes for events
        const loadEventNotes = async () => {
            try {
                await Promise.all(events.value.map(async event => {
                    try {
                        const notes = await noteService.fetchNotesForEntity('event', event.id);
                        eventNotes.value[event.id] = notes;
                    } catch (err) {
                        // Error loading notes for event
                    }
                }));
            } catch (err) {
                // Error loading notes
                error.value = 'Failed to load notes';
            }
        };

        // Check if event has notes
        const hasNotes = (eventId) => {
            return eventNotes.value[eventId]?.length > 0;
        };

        // Handle notes button click
        const handleNotes = (meeting) => {
            selectedEventForNotes.value = meeting;
            showNotesModal.value = true;
        };

        // Load notes when meetings are loaded
        watch(upcomingRegularMeetings, async (meetings) => {
            if (meetings?.length) {
                await loadEventNotes();
            }
        }, { immediate: true });

        // Initialize user from local storage
        onMounted(() => {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                user.value = JSON.parse(storedUser);
            }
        });

        const navigateToGoal = (goalId) => {
            router.push({ 
                name: 'Goals',
                query: { selectedGoalId: goalId }
            });
        };

        const handleNewTasks = (newTasks) => {
            // Add the new tasks to the existing tasks list
            tasks.value = [...tasks.value, ...newTasks];
        };

        return {
            user,
            userProfile,
            events,
            userDisplayName,
            greeting,
            upcomingRegularMeetings,
            upcomingOtherEvents,
            nextSpeaking,
            daysUntilNextSpeaking,
            isDueSoonToSpeak,
            lastSpoke,
            getTimeAgo,
            formatDate: eventService.formatDate,
            getEventTypeClass: (type) => {
                switch (type) {
                    case 'Network Event': return 'event-type-network';
                    case 'Social Event': return 'event-type-social';
                    default: return '';
                }
            },
            showDetailsModal,
            selectedEvent,
            viewEventDetails,
            closeDetailsModal,
            getEventSpeakerName,
            showCheckInModal,
            selectedMeeting,
            closeCheckInModal,
            handleCheckInComplete,
            openCheckIn,
            getMeetingCheckInStatus,
            getMeetingAttendanceStatus,
            showHeatMap,
            showHeatMapModal,
            closeHeatMapModal,
            navigateToGoal,
            memberGoals,
            tasks,
            hasNotes,
            handleNotes,
            showNotesModal,
            selectedEventForNotes,
            handleNewTasks,
            formats
        };
    }
};
</script>

<style scoped>
.dashboard {
    padding: 2rem;
    margin: 0 auto;
}

.greeting-section {
    margin-bottom: 2rem;
}

.greeting-section h2 {
    font-size: 2rem;
    color: #2c3e50;
    margin: 0;
}

.dashboard-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    box-sizing: border-box;
}

/* Left Column - Events Section */
.events-section {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.regular-meetings, .other-events {
    width: 100%;
    box-sizing: border-box;
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h3 {
    margin: 0 0 1rem 0;
    color: #2c3e50;
    font-size: 1.25rem;
}

.meetings-list, .events-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.meeting-item, .event-item {
    padding: 1rem;
    border-radius: 6px;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    cursor: pointer;
}

.meeting-date, .event-date {
    font-weight: 500;
    color: #2c3e50;
}

.meeting-venue, .event-venue {
    color: #666;
    font-size: 0.9rem;
}

.event-address {
    color: #888;
    font-size: 0.8rem;
    font-style: italic;
    margin-top: 0.25rem;
}

.event-type {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

.event-type-network {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.event-type-social {
    background-color: #f3e5f5;
    color: #7b1fa2;
}

/* Right Column - Speaking Info */
.speaking-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.next-speaking, .last-spoke {
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.next-speaking.soon {
    border: 2px solid #dc3545;
}

.next-speaking .date {
    font-size: 1.1rem;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 0.5rem;
}

.next-speaking .role {
    color: #666;
    margin-bottom: 0.5rem;
}

.next-speaking .days-until {
    color: #dc3545;
    font-weight: 500;
    font-size: 0.9rem;
}

.last-spoke {
    color: #666;
}

/* Responsive Design */
@media (min-width: 769px) {
    .dashboard {
        max-width: 1200px;
    }

    .dashboard-content {
        display: grid;
        grid-template-columns: 1.8fr 1.2fr;
        gap: 2rem;
        padding: 2rem;
    }
}

@media (max-width: 768px) {
    .dashboard {
        padding-top: 80px;
        padding-left: 1rem;
        padding-right: 1rem;
    }

    .dashboard-content {
        margin: 0;
    }
}

/* Empty State Styling */
.meetings-list:empty, .events-list:empty {
    padding: 1rem;
    text-align: center;
    color: #666;
    font-style: italic;
}

.meetings-list:empty::after {
    content: 'No upcoming regular meetings';
}

.events-list:empty::after {
    content: 'No upcoming events';
}

/* Loading State */
.loading {
    opacity: 0.7;
    pointer-events: none;
}

/* Hover Effects */
.meeting-item:hover, .event-item:hover {
    background: #f0f4f8;
    transform: translateY(-1px);
    transition: all 0.2s ease;
}

/* Additional Utility Classes */
.text-muted {
    color: #666;
}

.text-danger {
    color: #dc3545;
}

.font-bold {
    font-weight: 500;
}

/* Modal Styles */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    overflow-y: auto;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 900px;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
    position: relative;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
}

.cancel-btn {
    background-color: #6c757d;
    color: white;
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.cancel-btn:hover {
    background-color: #5a6268;
}

/* Event Details Grid Styles */
.event-details-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.detail-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
}

.details-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.detail-row {
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 1rem;
    align-items: baseline;
}

.detail-label {
    color: #666;
    font-weight: 500;
    font-size: 0.9rem;
    text-align: left;
}

.detail-value {
    color: #2c3e50;
    text-align: left;
}

.detail-value.description {
    grid-column: 1 / -1;
    margin-top: 0.5rem;
    padding: 0.75rem;
    background: #fff;
    border-radius: 4px;
    font-style: italic;
    text-align: left;
}

.sessions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
}

.detail-section h4 {
    margin: 0 0 1rem 0;
    color: #2c3e50;
    font-size: 1.1rem;
    text-align: left;
    padding-left: 0;
}

.meeting-item {
    background: #f5f5f5;  /* Very light gray for not checked in */
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
}

.meeting-item.not-checked-in {
    background: #f5f5f5;  /* Very light gray */
}

.meeting-item.checked-in-attending {
    background: #e8f5e9;  /* Light green */
}

.meeting-item.checked-in-not-attending {
    background-color: #ffebee;  /* Light red */
}

.meeting-item.checked-in-substitute {
    background-color: #fffde7;  /* Light yellow */
}

.meeting-header {
    position: relative;
    padding: 2rem 0.5rem 0.5rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    min-height: 80px;
}

.meeting-info {
    flex: 1;
    text-align: center;
    margin: 0 2.5rem;  /* Space for the icons on both sides */
}

.meeting-date {
    font-size: 1.1rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
    color: #2d3748;
}

.meeting-venue {
    font-size: 0.9rem;
    color: #718096;
}

.notes-btn {
    position: absolute;
    left: 0.5rem;
    top: 0.5rem;
    background: none;
    border: none;
    padding: 0.4rem;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    font-size: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    z-index: 1;
}

.notes-btn:hover {
    background: rgba(0, 0, 0, 0.05);
}

.notes-btn.has-notes {
    background: rgba(52, 152, 219, 0.1);
}

.notes-btn.has-notes:hover {
    background: rgba(52, 152, 219, 0.2);
}

.heat-map-btn {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    background: none;
    border: none;
    padding: 0.25rem;
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.2s ease;
    font-size: 1.2rem;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

.meeting-footer {
    display: grid;
    grid-template-columns: auto 1fr 120px;
    align-items: center;
    gap: 1rem;
}

.right-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.view-details-btn {
    background: #6c757d;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
}

.check-in-wrapper {
    text-align: right;
}

.check-in-btn {
    width: 100%;
    background: #4285f4;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    white-space: nowrap;
}

.check-in-btn.edit-mode {
    background: #757575;
}

.check-in-btn:hover {
    background: #3367d6;
}

.check-in-btn.edit-mode:hover {
    background: #616161;
}

.check-in-status {
    color: #2e7d32;  /* Dark green text */
    font-size: 0.875rem;
    font-weight: 500;
}

.meeting-item:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
    transition: all 0.2s ease;
}

.meeting-item.checked-in-attending:hover {
    background: #c8e6c9;
}

.meeting-item.checked-in-not-attending:hover {
    background-color: #ffcdd2;
}

.meeting-item.checked-in-substitute:hover {
    background-color: #fff9c4;
}

.check-in-history {
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Update the graph component styles */
.check-in-history :deep(.score-graph) {
    height: 200px;
    margin: 0;
    padding: 0;
}

.meeting-header {
    position: relative;
}

.heat-map-btn {
    position: absolute;
    top: 0;
    right: 0;
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 4px;
    transition: transform 0.2s;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

@media (max-width: 768px) {
    .heat-map-btn {
        top: 24px;
        right: 4px;
    }

    .meeting-header {
        padding-right: 32px;
    }
}

.heat-map-btn:hover {
    transform: scale(1.1);
}

.check-in-btn.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #757575;
}

.check-in-btn.disabled:hover {
    background: #757575;
}

/* Birthday section styles */
.upcoming-birthdays {
    width: 100%;
    box-sizing: border-box;
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.birthday-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
}

/* Force vertical stacking on mobile */
@media (max-width: 768px) {
    .birthday-cards {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .birthday-card {
        width: 100%;
        margin-right: 0;
    }
}

.birthday-card {
    width: 100%;
    box-sizing: border-box;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.tasks-summary-widget {
    margin-bottom: 1.5rem;

}

    .notes-btn {
    position: absolute;
    left: 0.5rem;
    top: 0.5rem;
    background: none;
    border: none;
    padding: 0.4rem;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    font-size: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    z-index: 1;
}

.notes-btn:hover {
    background: rgba(0, 0, 0, 0.05);
}

.notes-btn.has-notes {
    background: rgba(52, 152, 219, 0.1);
}

.notes-btn.has-notes:hover {
    background: rgba(52, 152, 219, 0.2);
}

.heat-map-btn {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    background: none;
    border: none;
    padding: 0.25rem;
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.2s ease;
    font-size: 1.2rem;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}
</style>